import React, { useState, useEffect } from 'react';
import { 
  BookOpen, Calendar, User, Tag, Shield, ChevronRight, ChevronLeft,
  Star, Zap, Search, Clock, ArrowRight, ArrowLeft, ChevronDown, ChevronUp 
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Footer } from '../Footer';
import { PublicHeader } from '../PublicHeader';
import { supabase } from '../../lib/supabase';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PreviewComponents } from '../shared/MarkdownComponents';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  featured: boolean;
  read_time: string;
  content: string;
  keywords: string[];
  published: boolean;
  comingSoon?: boolean;
}

export function Blog() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentFeaturedIndex, setCurrentFeaturedIndex] = useState(0);
  const [showUpcoming, setShowUpcoming] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('published', true)
        .order('date', { ascending: false });

      if (error) throw error;

      const currentDate = new Date();
      const availablePosts = data.map(post => ({
        ...post,
        comingSoon: new Date(post.date) > currentDate
      }));

      setPosts(availablePosts);
    } catch (error) {
      console.error('Error loading blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    'All',
    'Proposal Writing',
    'Pricing Strategies',
    'Compliance',
    'AI & Technology',
    'Contracting',
    'Industry Insights'
  ];

  const publishedPosts = posts.filter(post => !post.comingSoon);
  const upcomingPosts = posts.filter(post => post.comingSoon);

  const filteredPublishedPosts = publishedPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || post.category.toLowerCase() === selectedCategory;
    return matchesSearch && matchesCategory;
  }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()); // Sort newest to oldest

  const filteredUpcomingPosts = upcomingPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || post.category.toLowerCase() === selectedCategory;
    return matchesSearch && matchesCategory;
  }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()); // Sort by release date ascending

  const featuredPosts = posts.filter(post => post.featured && !post.comingSoon);

  const nextFeatured = () => {
    setCurrentFeaturedIndex(current => 
      current === featuredPosts.length - 1 ? 0 : current + 1
    );
  };

  const prevFeatured = () => {
    setCurrentFeaturedIndex(current => 
      current === 0 ? featuredPosts.length - 1 : current - 1
    );
  };

  const handlePostClick = (post: BlogPost) => {
    if (!post.comingSoon) {
      navigate(`/blog/${post.id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bw-surface-subtle">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-servet-blue"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-bw-app">
      <PublicHeader />
      
      <div className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="relative bg-bw-sidebar rounded-lg shadow-lg p-12 mb-12 overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
            </div>

            <div className="relative flex items-center justify-center flex-col">
              <div className="flex items-center justify-center space-x-4 mb-6">
                <Zap className="h-12 w-12 text-bw-accent" />
                <div className="text-white">
                  <h1 className="text-4xl font-bold">The Winning Bid</h1>
                  <div className="text-sm opacity-80">by SerVet Digital</div>
                </div>
              </div>
              
              <p className="text-center text-white/90 max-w-2xl mx-auto mb-8">
                Expert insights, trends, and strategies for small businesses in federal contracting
              </p>
              
              <div className="w-full max-w-xl mx-auto">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white placeholder-white/60 focus:ring-2 focus:ring-servet-red focus:border-transparent"
                  />
                  <Search className="absolute left-4 top-3.5 h-5 w-5 text-white/60" />
                </div>
              </div>
            </div>
          </div>

          {/* Featured Articles Slider */}
          {featuredPosts.length > 0 && (
            <div className="mb-12 relative">
              <div className="relative bg-bw-sidebar rounded-xl shadow-xl overflow-hidden">
                <div className="relative p-12">
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
                  </div>
                  
                  <div className="relative text-center text-white">
                    <div className="flex items-center justify-center text-sm text-white/80 mb-4">
                      <Star className="h-5 w-5 mr-2" />
                      <span className="font-semibold">Featured Article</span>
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-4">
                      {featuredPosts[currentFeaturedIndex].title}
                    </h2>
                    <p className="text-white/90 mb-6 line-clamp-3">
                      {featuredPosts[currentFeaturedIndex].excerpt}
                    </p>
                    <div className="flex flex-col items-center space-y-4">
                      <div className="flex items-center space-x-4 text-white/80">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2" />
                          <span>{featuredPosts[currentFeaturedIndex].date}</span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-2" />
                          <span>{featuredPosts[currentFeaturedIndex].read_time}</span>
                        </div>
                      </div>
                      <Link 
                        to={`/blog/${featuredPosts[currentFeaturedIndex].id}`}
                        className="inline-flex items-center px-6 py-2 bg-servet-red text-white rounded-lg hover:bg-servet-red/90 transition-colors group"
                      >
                        Read Article
                        <ChevronRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Buttons */}
              {featuredPosts.length > 1 && (
                <>
                  <button
                    onClick={prevFeatured}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors"
                    aria-label="Previous featured article"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    onClick={nextFeatured}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors"
                    aria-label="Next featured article"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>

                  {/* Dots Indicator */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                    {featuredPosts.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentFeaturedIndex(index)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentFeaturedIndex 
                            ? 'bg-bw-surface' 
                            : 'bg-white/50 hover:bg-white/75'
                        }`}
                        aria-label={`Go to featured article ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category.toLowerCase())}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all transform hover:scale-105 ${
                  selectedCategory === category.toLowerCase()
                    ? 'bg-bw-primary text-white shadow-lg'
                    : 'bg-bw-surface text-bw-muted hover:bg-bw-primary-tint hover:text-bw-primary shadow-card'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Published Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPublishedPosts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.id}`}
                className="group bg-bw-surface rounded-xl shadow-lg overflow-hidden transform hover:scale-[1.02] transition-all duration-300"
              >
                <div className="relative bg-bw-sidebar p-8">
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
                  </div>

                  <div className="relative">
                    <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">
                      {post.title}
                    </h3>
                    <div className="flex items-center text-sm text-white/80 space-x-2">
                      <span className="truncate">{post.author}</span>
                      <span className="text-bw-primary font-medium">{post.read_time}</span>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <p className="text-bw-muted text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="flex items-center">
                      <Tag className="h-4 w-4 text-bw-primary mr-2" />
                      <span className="text-sm font-medium text-bw-primary">
                        {post.category}
                      </span>
                    </div>
                    <div className="text-sm text-bw-primary hover:text-bw-primary/80 flex items-center group">
                      Read More
                      <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Upcoming Articles Section */}
          {filteredUpcomingPosts.length > 0 && (
            <div className="mt-16">
              <button
                onClick={() => setShowUpcoming(!showUpcoming)}
                className="w-full flex items-center justify-between px-6 py-4 bg-bw-primary-tint rounded-lg hover:bg-bw-primary-tint/80 transition-colors"
              >
                <div className="flex items-center">
                  <Calendar className="h-5 w-5 text-bw-primary mr-2" />
                  <span className="text-lg font-semibold text-bw-primary">
                    Upcoming Articles ({filteredUpcomingPosts.length})
                  </span>
                </div>
                {showUpcoming ? (
                  <ChevronUp className="h-5 w-5 text-bw-primary" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-bw-primary" />
                )}
              </button>

              {showUpcoming && (
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredUpcomingPosts.map((post) => (
                    <div
                      key={post.id}
                      className="bg-bw-surface rounded-lg shadow-card p-6 opacity-75"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-medium text-bw-primary px-3 py-1 bg-bw-primary-tint rounded-full">
                          Coming {post.date}
                        </span>
                        <Tag className="h-4 w-4 text-bw-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-bw-ink mb-2">
                        {post.title}
                      </h3>
                      <p className="text-bw-muted text-sm line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="mt-4 pt-4 border-t">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-bw-muted">
                            {post.category}
                          </span>
                          <span className="text-sm text-bw-primary">
                            {post.read_time}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-16 relative">
            <div className="absolute inset-0 bg-bw-sidebar rounded-xl"></div>
            <div className="relative bg-bw-sidebar rounded-xl shadow-xl p-12 text-center overflow-hidden">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
              </div>
              
              <div className="relative">
                <Zap className="h-16 w-16 text-bw-accent mx-auto mb-6 animate-pulse" />
                <h3 className="text-3xl font-bold text-white mb-4">
                  Stay Updated with The Winning Bid
                </h3>
                <p className="text-white/90 mb-8 max-w-2xl mx-auto">
                  Subscribe to our newsletter for the latest insights, tips, and strategies in government contracting.
                </p>
                <form className="max-w-md mx-auto flex flex-col gap-4">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-servet-red focus:border-transparent"
                  />
                  <button
                    type="submit"
                    className="w-full px-8 py-3 bg-servet-red text-white rounded-lg hover:bg-servet-red/90 transition-colors transform hover:scale-105"
                  >
                    Subscribe
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="mt-16 bg-bw-surface-subtle border border-bw-border rounded-xl p-8">
            <div className="flex items-start max-w-3xl mx-auto">
              <Shield className="h-6 w-6 text-bw-primary mt-1 mr-4 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-bw-ink mb-2">Legal Disclaimer</h4>
                <p className="text-sm text-bw-muted">
                  This blog is for informational purposes only and does not constitute legal or business advice. 
                  Always consult with a professional advisor before making business decisions.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-xs text-bw-muted">
              Keywords: government contracting, federal contracts, small business, AI for proposals, 
              govcon trends, proposal automation, bid pricing, small business set-asides, compliance for contractors
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}